/**
 * G-09 · history imported from spreadsheets, and G-43 · the statistics check — their door to the
 * server (`/bp/cpv/batches/imports/…`, `/bp/cpv/plans/<id>/parity/…`; cpv_backend CPV-G25-DATASHEET §21).
 *
 * The S10 transport (cpvMasterApi.js) is reused: the envelope unwrapped, a list is one page `{results, count, page,
 * page_size, pages}`, and the three acts (commit, verify, void) carry an Idempotency-Key kept across retries of the same body (the
 * e-signature aside). Inspect and preview are read-only POSTs (the worksheet travels in the body as base64).
 *
 * The client decides nothing: the suggested mapping, every row check, `can_commit`, the purpose, the e-sign meaning, the
 * import's status (PENDING until a second person verifies it), the acts offered (`allowed_acts`) and every statistic of
 * the parity report are the server's.
 */
import Axios from 'axios';
import { get, post } from './cpvMasterApi';

/** An import's status as the server states it (PENDING · VERIFIED · VOIDED): [tone, label]. The import pages and the batch
 * register (a batch an import registered carries `import_status`) say it in the same words. */
export const IMPORT_STATUS = { PENDING: ['warning', 'Pending verification'], VERIFIED: ['ok', 'Verified'], VOIDED: ['neutral', 'Voided'] };

const IMPORTS = '/cpv/batches/imports';
const BP = '/bp';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('Token') || ''}` });
const unwrap = (r) => r.data?.data ?? r.data;
/** A read-only POST: no Idempotency-Key (the server writes nothing). */
const read = (path, body, params) => Axios.post(`${BP}${path}`, body, { params, headers: auth() }).then(unwrap);

/** A File → its base64 content (no data: prefix), as the server reads `file_base64`. */
export function fileBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',', 2)[1] || '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

/** Save a server file download (CSV / PDF / the worksheet) under the server's own file name. */
export async function download(path, params, fallbackName) {
  const r = await Axios.get(`${BP}${path}`, { params, headers: auth(), responseType: 'blob' });
  const cd = r.headers?.['content-disposition'] || '';
  const name = /filename="([^"]+)"/.exec(cd)?.[1] || fallbackName;
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export const historyApi = {
  /** The plant's imports (a page). */
  list: (params) => get(`${IMPORTS}/`, params),
  /** {plan, file_name, file_base64, parameter_column?} → header, units, sample rows, importable parameters, suggestion. */
  inspect: (body) => read(`${IMPORTS}/inspect/`, body),
  /** {plan, file_name, file_base64, mapping} → summary, new batches, purpose, can_commit, esign_meaning, issues (a page). */
  preview: (body, params) => read(`${IMPORTS}/preview/`, body, params),
  /** The signed commit: the same body + {reason, esign, meanings_shown}. */
  commit: (body) => post(`${IMPORTS}/`, body),
  /** One import with its values (a page). */
  detail: (id, params) => get(`${IMPORTS}/${id}/`, params),
  /** The worksheet file as imported. */
  file: (id, name) => download(`${IMPORTS}/${id}/file/`, undefined, name),
  /** The second person's signed verification: {comment, esign, meanings_shown} (never the importer — the server refuses). */
  verify: (id, body) => post(`${IMPORTS}/${id}/verify/`, body),
  /** The imports waiting on my verification (CPV My Actions, kind HISTORY_VERIFY). */
  awaitingMe: () => get('/cpv/my-actions/', { kind: 'HISTORY_VERIFY' }),
  /** The signed void: {reason, esign, meanings_shown}. */
  void: (id, body) => post(`${IMPORTS}/${id}/void/`, body),
};

/** CPV-Q13 · a server file (a source page PNG, the document) as an object URL the caller revokes. */
async function blobUrl(path) {
  const r = await Axios.get(`${BP}${path}`, { headers: auth(), responseType: 'blob' });
  return URL.createObjectURL(r.data);
}

const DOCS = `${IMPORTS}/documents`;
const EXTRACTIONS = `${IMPORTS}/extractions`;

/**
 * CPV-Q13 · history from a PDF or a scanned page. The server stores the document unchanged, proposes the table (an
 * engine's reading), records each person's confirmation or correction, and decides `can_commit` — every value the
 * mapping reads confirmed by the importer. The commit carries an Idempotency-Key; verify and void are the import's own.
 */
export const documentApi = {
  /** The engines, whether each can run here, and the plant's configured choice. */
  engines: () => get(`${DOCS}/engines/`),
  /** Store the source document (multipart `file`, `plant?`): 201 stored now, 200 the same file already stored. */
  upload: (file, plant) => {
    const form = new FormData();
    form.append('file', file);
    if (plant != null) form.append('plant', String(plant));
    return Axios.post(`${BP}${DOCS}/`, form, { headers: auth() }).then(unwrap);
  },
  /** Run an engine ({engine?}) → the proposal with every cell to confirm. */
  extract: (id, engine) => Axios.post(`${BP}${DOCS}/${id}/extract/`, engine ? { engine } : {}, { headers: auth() }).then(unwrap),
  extraction: (id) => get(`${EXTRACTIONS}/${id}/`),
  /** Confirm / correct: {cells: [{row, col, value?}]} or {page}. */
  check: (id, body) => Axios.post(`${BP}${EXTRACTIONS}/${id}/check/`, body, { headers: auth() }).then(unwrap),
  /** Exclude / include rows: {rows: [{row, excluded, reason}]}. */
  rows: (id, body) => Axios.post(`${BP}${EXTRACTIONS}/${id}/rows/`, body, { headers: auth() }).then(unwrap),
  inspect: (id, body) => read(`${EXTRACTIONS}/${id}/inspect/`, body),
  preview: (id, body, params) => read(`${EXTRACTIONS}/${id}/preview/`, body, params),
  /** The signed commit: {plan, mapping, unit?, reason, esign, meanings_shown}. */
  commit: (id, body) => post(`${EXTRACTIONS}/${id}/commit/`, body),
  /** Page `page` of document `id` as an object URL (PNG). */
  pageUrl: (id, page) => blobUrl(`${DOCS}/${id}/pages/${page}/`),
  /** The document as uploaded, opened in a new tab at `page` (a PDF viewer honours #page). */
  openFile: async (id, page) => {
    const url = await blobUrl(`${DOCS}/${id}/file/`);
    window.open(page ? `${url}#page=${page}` : url, '_blank', 'noopener');
  },
  /** Imported values with their source page: {batch_number} or {ids: '1,2'}. */
  values: (params) => get(`${IMPORTS}/values/`, params),
};

export const parityApi = {
  /** The statistics check of a plan (every active item, or `item`). */
  report: (planId, item) => get(`/cpv/plans/${planId}/parity/`, item ? { item } : undefined),
  /** The same report as a file: csv | pdf (the query word is `file`; DRF keeps `format` for itself). */
  export: (planId, format, item) => download(`/cpv/plans/${planId}/parity/export/`, { file: format, ...(item ? { item } : {}) },
    `cpv-statistics-check-plan-${planId}.${format}`),
};
